/**
 * Bridge Resource
 *
 * Wraps `/v1/bridge/...` HTTP control-plane endpoints (status, connected
 * devices) plus the factory for the web cloud bridge client
 * ({@link BridgeEventStream} via {@link events}). The stream is the web-only
 * realtime path (session events, platform_call, indexing, notifications,
 * cloud filesChanged); desktop uses the daemon-owned bridge + `/events` relay
 * instead of opening this socket from the renderer.
 */

import type { HttpLayer, SDKConfig } from '../http.js';
import type { WebSocketFactory, WebSocketLike } from '../chat/types.js';
import {
  BridgeEventStream,
  type BridgeIdentity,
  type WorkspaceProjectionRegistrar,
} from './bridge-events.js';

export interface BridgeStatus {
  device_id: string;
  /** "CONNECTED" when a fresh active_connections row names a live socket, else "DISCONNECTED". */
  status: string;
  /** Whether the bridge is currently holding an active WebSocket connection. */
  connected: boolean;
}

export interface ConnectedDevice {
  device_id: string;
  name: string;
  kind: 'desktop' | 'web';
  connected_at: string;
  last_heartbeat: string;
}

export class BridgeResource {
  constructor(
    private http: HttpLayer,
    private config: SDKConfig,
    private workspaceProjectionRegistrar?: WorkspaceProjectionRegistrar,
  ) {}

  /**
   * Open the web cloud bridge client (`/ws/bridge`) for the given tenant
   * identity. The returned {@link BridgeEventStream} is the shared per-tab
   * realtime transport for session events, `platform_call` (`mcp.invoke`),
   * `search.indexingProgress`, notification/error push, and
   * `workspace.filesChanged` — not chat alone.
   *
   * The caller supplies the tenant identity (deviceId/org/account/token)
   * because the SDK is stateless about claims — those live in the consumer's
   * auth store. The WebSocket factory defaults to the one in SDKConfig, or the
   * platform-global `WebSocket` in a browser. Throws if neither is available
   * (no silent no-op — a missing transport is a real misconfiguration).
   */
  events(identity: BridgeIdentity, wsFactory?: WebSocketFactory): BridgeEventStream {
    const globalWs = (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
    const factory: WebSocketFactory | undefined =
      wsFactory ??
      this.config.webSocketFactory ??
      (globalWs
        ? (url, protocols) => new globalWs(url, protocols) as unknown as WebSocketLike
        : undefined);
    if (!factory) {
      throw new Error(
        'bridge.events requires a WebSocket — pass `webSocketFactory` in SDKConfig, ' +
          'or run in a context with a global WebSocket',
      );
    }
    return new BridgeEventStream(
      this.config.baseUrl,
      identity,
      factory,
      undefined,
      undefined,
      this.workspaceProjectionRegistrar,
    );
  }

  /** Get the bridge status for a given device instance. */
  async getStatus(deviceId: string): Promise<BridgeStatus> {
    return this.http.get(`/v1/bridge/devices/${deviceId}/status`);
  }

  /**
   * List devices currently connected to the bridge for the caller's account.
   * Used by clients to discover available local agents (ShogunChop instances)
   * without talking to ShogunChop directly.
   */
  async listConnectedDevices(): Promise<ConnectedDevice[]> {
    return this.http.get('/v1/bridge/devices/connected');
  }

  /**
   * Whether the device's bridge connection is live and usable right now.
   *
   * This reads the workflow-backed `connected` flag from {@link getStatus} —
   * the authoritative signal that device-routed requests (file tools, MCP
   * calls, LLM completions) will actually reach the daemon. Note this is
   * stricter than {@link listConnectedDevices}, which reflects the
   * `active_connections` table and can briefly report a device whose
   * connection workflow has not yet come up after a cold start.
   */
  async isConnected(deviceId: string): Promise<boolean> {
    const status = await this.getStatus(deviceId);
    return status.connected;
  }

  /**
   * Poll {@link getStatus} until the device reports `connected`, then resolve
   * with the final status. Throws if the device does not become connected
   * within `timeoutMs`.
   *
   * Intended as a precondition gate for callers that route work to a device
   * (tests exercising local tools, client UI waiting for a daemon to come up).
   * It deliberately fails loudly rather than resolving to a "not connected"
   * value, so a never-connecting device surfaces as an explicit error instead
   * of a silently degraded path.
   */
  async waitForConnected(
    deviceId: string,
    opts: { timeoutMs?: number; intervalMs?: number } = {},
  ): Promise<BridgeStatus> {
    const timeoutMs = opts.timeoutMs ?? 30_000;
    const intervalMs = opts.intervalMs ?? 500;
    const deadline = Date.now() + timeoutMs;

    let last: BridgeStatus | undefined;
    for (;;) {
      last = await this.getStatus(deviceId);
      if (last.connected) return last;
      if (Date.now() >= deadline) break;
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error(
      `bridge device ${deviceId} did not report connected within ${timeoutMs}ms ` +
        `(last status: ${last?.status ?? 'unknown'})`,
    );
  }
}
